<template>
  <div class="subscriptions-panel" v-if="!hideEmpty || subscriptions.length || charges.length">
    <div class="flex-row space-between">
      <h3>{{ _('Subscriptions') }}</h3>
      <div class="flex-row" style="gap: 5px;" v-if="!orderId">
        <button class="btn btn-success" type="button" v-on:click="createSubscription"><i class="fa fa-fw fa-plus"></i><span class="hidden-xs"> {{ _('New subscription') }}</span></button>
      </div>
    </div>
    <p v-if="loading && !loaded"><i class="fa fa-fw fa-spinner fa-spin"></i> {{ _('Loading...') }}</p>
    <p v-else-if="!subscriptions.length" class="text-muted">{{ _('No subscriptions.') }}</p>
    <div class="table-responsive" v-else>
      <table class="table table-striped table-hover">
        <thead>
          <tr>
            <th>{{ _('ID') }}</th>
            <th>{{ _('Name') }}</th>
            <th>{{ _('Status') }}</th>
            <th>{{ _('Valid until') }}</th>
            <th>{{ _('Next billing') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in subscriptions" :key="s.id" class="clickable" v-on:click="openSubscription(s)">
            <td>{{ s.id }}</td>
            <td>{{ s.name }}</td>
            <td><span :class="'label label-' + statusClass(s)">{{ statusName(s) }}</span></td>
            <td>{{ formatLastDay(s.valid_until_ts) }}</td>
            <td>{{ formatDate(s.next_billing_ts) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <template v-if="showCharges">
      <div class="flex-row space-between">
        <h3>{{ _('Charges for the next bill') }}</h3>
        <button class="btn btn-success" type="button" v-on:click="addChargeDialog"><i class="fa fa-fw fa-plus"></i><span class="hidden-xs"> {{ _('Add charge') }}</span></button>
      </div>
      <p class="help-block">{{ _('One-off charges go on the payer\'s next recurring bill if one is due within a month of the charge\'s due date. Otherwise they are billed on their own.') }}</p>
      <p v-if="!charges.length" class="text-muted">{{ _('No pending charges.') }}</p>
      <div class="table-responsive" v-else>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>{{ _('Product name') }}</th>
              <th>{{ _('Amount') }}</th>
              <th>{{ _('Unit price (inc. vat)') }}</th>
              <th>{{ _('VAT %') }}</th>
              <th>{{ _('Due date') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in charges" :key="c.id">
              <td>{{ c.product_name }}</td>
              <td>{{ c.count }}</td>
              <td>{{ formatPrice(c.price) }}</td>
              <td>{{ formatVat(c.vat, numberFormat) }}</td>
              <td>{{ formatDate(c.due_ts) }}</td>
              <td class="text-right"><button class="btn btn-danger btn-xs" type="button" v-on:click="removeChargeDialog(c)" :title="_('Delete')"><i class="fa fa-fw fa-trash"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>

import axios from 'axios'
import errors from '~/lib/errors'
import formats from '~/lib/formats'
import modals from '~/lib/modals'
import subscriptionhelpers from '~/lib/subscriptionhelpers'
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'

export default {
  props: {
    billingCustomerId: { default: null },
    customerId: { default: null },
    orderId: { default: null },
    showCharges: { type: Boolean, default: false },
    hideEmpty: { type: Boolean, default: false },
    detailsTrail: { type: Array, default: null }
  },
  data () {
    return {
      loading: false,
      loaded: false,
      subscriptions: [],
      charges: []
    }
  },
  mixins: [errors, formats, modals, subscriptionhelpers],
  computed: {
    ...mapGetters(['baseDomain', 'shopId', 'apiKey', 'numberFormat']),
    filterParams () {
      const params = []
      if (this.billingCustomerId) {
        params.push('billingcustomer_id=' + encodeURIComponent(this.billingCustomerId))
      }
      if (this.customerId) {
        params.push('customer_id=' + encodeURIComponent(this.customerId))
      }
      if (this.orderId) {
        params.push('order_id=' + encodeURIComponent(this.orderId))
      }
      return params
    },
    hasFilter () {
      return this.filterParams.length > 0
    }
  },
  methods: {
    async load () {
      if (!this.hasFilter) {
        return
      }
      this.loading = true
      try {
        const query = this.filterParams.join('&') + '&apikey=' + this.apiKey
        const requests = [axios.get(this.baseDomain + this.shopId + '/subscriptions.json?' + query)]
        if (this.showCharges && !this.orderId) {
          requests.push(axios.get(this.baseDomain + this.shopId + '/pendingcharges.json?billed=0&' + query))
        }
        const [subscriptions, charges] = await Promise.all(requests)
        this.subscriptions = Array.isArray(subscriptions.data) ? subscriptions.data : Object.values(subscriptions.data || {})
        this.charges = charges ? (Array.isArray(charges.data) ? charges.data : Object.values(charges.data || {})) : []
      } catch (e) {
        this.showError(e)
      }
      this.loading = false
      this.loaded = true
    },
    openSubscription (s) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'SubscriptionDetails',
        title: this._('Subscription') + ' ' + s.id + (s.name ? ': ' + s.name : ''),
        width: 6,
        data: {
          subscriptionId: s.id,
          trail: this.detailsTrail || []
        }
      })
    },
    createSubscription () {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'SubscriptionDetails',
        title: this._('New subscription'),
        width: 6,
        data: {
          billingCustomerId: this.billingCustomerId,
          customerId: this.customerId,
          trail: this.detailsTrail || []
        }
      })
    },
    addChargeDialog () {
      this.showDialog({
        title: this._('Add charge'),
        width: 700,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Add'),
            class: 'success',
            icon: 'save',
            requiredFields: ['product_name', 'price', 'vat', 'count'],
            handler: this.addCharge
          }
        ],
        fields: [
          { title: this._('Product name'), type: 'text', name: 'product_name' },
          { title: this._('Unit price (inc. vat)'), type: 'number', name: 'price' },
          { title: this._('VAT %'), type: 'number', name: 'vat' },
          { title: this._('Amount'), type: 'number', name: 'count', defaultValue: 1 },
          { title: this._('Due date'), type: 'date', name: 'due_date', defaultValue: new Date(), help: this._('The charge is not billed before this date.') }
        ]
      })
    },
    async addCharge (data) {
      const payload = {
        product_name: data.product_name,
        price: data.price,
        vat: data.vat,
        count: data.count,
        due_date: data.due_date ? DateTime.fromJSDate(data.due_date).toISODate() : null
      }
      if (this.billingCustomerId) {
        payload.billingcustomer_id = this.billingCustomerId
      } else if (this.customerId) {
        payload.customer_id = this.customerId
      }
      try {
        await axios.post(this.baseDomain + this.shopId + '/pendingcharges.json?apikey=' + this.apiKey, payload)
      } catch (e) {
        return this.showError(e)
      }
      this.showNotification(this._('Charge added.'))
      this.load()
    },
    removeChargeDialog (charge) {
      this.showDialog({
        title: this._('Delete the charge %s?').replace('%s', charge.product_name),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Delete'),
            class: 'danger',
            icon: 'trash',
            handler: () => this.removeCharge(charge)
          }
        ]
      })
    },
    async removeCharge (charge) {
      try {
        await axios.delete(this.baseDomain + this.shopId + '/pendingcharges/' + charge.id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        return this.showError(e)
      }
      this.load()
    }
  },
  watch: {
    filterParams () {
      this.load()
    }
  },
  mounted () {
    this.load()
    this.subscriptionUpdateHandler = () => this.load()
    this.EventBus.$on('subscriptionUpdate', this.subscriptionUpdateHandler)
  },
  beforeUnmount () {
    this.EventBus.$off('subscriptionUpdate', this.subscriptionUpdateHandler)
  }
}
</script>
